/**
 * Re-export of the shared template renderer.
 *
 * The grid maths and inline-style conversion are part of the contract that the
 * theme builder and this storefront must agree on, so they now live in one place:
 * the generated copy in ../renderer, whose source is
 * couponbase-builder/packages/template-renderer.
 *
 * The implementations moved; this module's public API did not, so the components
 * importing from here need no change.
 *
 * Edit the source in the builder repo and run `pnpm sync:renderer` there. A test
 * fails if ../renderer drifts from that source.
 */
export {
    convertStylesToInline,
    getColumnClasses,
    getColumnFlexStyle,
    getGridClasses,
    getGridStyle,
    getGridWithGapClasses,
    getItemsLayoutStyle,
} from '../renderer';
import type { CSSProperties } from 'react';
import type { ComponentNode, GridSize } from '../types/template';
import { getItemsLayoutStyle as getSharedItemsLayoutStyle } from '../renderer';

export function isResponsiveSplitLayout(component: ComponentNode): boolean {
    if (component.type !== 'CONTAINER' || component.components?.length !== 2) {
        return false;
    }

    const styles = component.styles;
    if (styles?.display === 'flex' && styles.flexWrap === 'wrap') {
        const children = component.components.map((child) => String(child.styles?.flex ?? ''));
        return children.some((flex) => /^0\s+0\s+/.test(flex)) && children.some((flex) => /^1\s+1\s+/.test(flex));
    }

    return styles?.display === 'grid'
        && typeof styles.gridTemplateColumns === 'string'
        && /^\s*\d+(?:\.\d+)?(?:px|rem|em)\s+(?:minmax\(.+\)|\d+(?:\.\d+)?fr)\s*$/.test(styles.gridTemplateColumns);
}

export function isResponsiveSidebar(component: ComponentNode): boolean {
    return component.styles?.position === 'sticky'
        && Boolean(component.styles.width)
        && Boolean(component.styles.maxWidth);
}


/** CustomTheme-only responsive grid styles; the builder keeps its own renderer behavior. */
const columnsForSpan = (span?: number) => Math.max(1, Math.min(12, Math.floor(12 / (span || 12))));

export function getCustomThemeGridStyle(gridCols?: GridSize, gridGap?: number): CSSProperties {
    const columns = (span?: number) => columnsForSpan(span);
    return {
        display: 'grid',
        gridTemplateColumns: 'repeat(var(--cb-grid-columns), minmax(0, 1fr))',
        '--cb-grid-xs-columns': columns(gridCols?.xs),
        '--cb-grid-sm-columns': columns(gridCols?.sm ?? gridCols?.xs),
        '--cb-grid-md-columns': columns(gridCols?.md ?? gridCols?.sm ?? gridCols?.xs),
        '--cb-grid-lg-columns': columns(gridCols?.lg ?? gridCols?.md ?? gridCols?.sm ?? gridCols?.xs),
        gap: `${gridGap || 24}px`,
    } as CSSProperties;
}

export function getCustomThemeItemsLayoutStyle(
    itemsLayout: 'grid' | 'flex-wrap' | 'flex-nowrap' | undefined,
    gridCols?: GridSize,
    gridGap?: number,
): CSSProperties {
    if (itemsLayout === 'flex-wrap' || itemsLayout === 'flex-nowrap') {
        return getSharedItemsLayoutStyle(itemsLayout, gridCols, gridGap);
    }
    return getCustomThemeGridStyle(gridCols, gridGap);
}

export function getCustomThemeColumnFlexStyle(size?: GridSize): CSSProperties {
    const widthFor = (span?: number) => {
        const activeSpan = span || 12;
        const widthPercent = `${(activeSpan / 12) * 100}%`;
        return activeSpan === 12 ? widthPercent : `calc(${widthPercent} - (var(--custom-theme-column-gap, 0px) / 2))`;
    };
    return {
        '--cb-column-xs-width': widthFor(size?.xs),
        '--cb-column-sm-width': widthFor(size?.sm ?? size?.xs),
        '--cb-column-md-width': widthFor(size?.md ?? size?.sm ?? size?.xs),
        '--cb-column-lg-width': widthFor(size?.lg ?? size?.md ?? size?.sm ?? size?.xs),
        minWidth: 0,
        boxSizing: 'border-box',
    } as CSSProperties;
}
